import versesData from '../data/verses.json';

export interface Verse {
  id: number;
  reference: string;
  text: string;
}

// 모든 말씀 데이터
export const ALL_VERSES: Verse[] = versesData;

// 로컬 스토리지 키
const USED_VERSES_KEY = 'usedVerses';
const CURRENT_VERSE_KEY = 'currentVerse';
const LAST_RESET_DATE_KEY = 'lastResetDate';

// 오늘 날짜를 YYYY-MM-DD 형식으로 반환
const getTodayString = (): string => {
  return new Date().toISOString().split('T')[0];
};

// 사용된 말씀 ID 목록 가져오기
const getUsedVerses = (): number[] => {
  try {
    const stored = localStorage.getItem(USED_VERSES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// 사용된 말씀 ID 목록 저장
const setUsedVerses = (usedIds: number[]): void => {
  localStorage.setItem(USED_VERSES_KEY, JSON.stringify(usedIds));
};

// 현재 말씀 저장
const setCurrentVerse = (verse: Verse): void => {
  localStorage.setItem(CURRENT_VERSE_KEY, JSON.stringify(verse));
};

// 현재 말씀 가져오기
const getCurrentVerse = (): Verse | null => {
  try {
    const stored = localStorage.getItem(CURRENT_VERSE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// 마지막 리셋 날짜 확인
const getLastResetDate = (): string => {
  return localStorage.getItem(LAST_RESET_DATE_KEY) || '';
};

// 마지막 리셋 날짜 저장
const setLastResetDate = (date: string): void => {
  localStorage.setItem(LAST_RESET_DATE_KEY, date);
};

// 날짜가 바뀌었는지 확인하고 필요시 리셋
const checkAndResetIfNewDay = (): void => {
  const today = getTodayString();
  const lastReset = getLastResetDate();
  
  if (lastReset !== today) {
    // 새로운 날이므로 사용된 말씀 목록과 현재 말씀 초기화
    localStorage.removeItem(USED_VERSES_KEY);
    localStorage.removeItem(CURRENT_VERSE_KEY);
    setLastResetDate(today);
    console.log('✨ 새로운 날 - 말씀 목록 초기화됨');
  }
};

// 랜덤 말씀 선택 (중복 방지)
export const getRandomVerse = (): Verse => {
  checkAndResetIfNewDay();
  
  // 현재 말씀이 있다면 그것을 반환 (하루 동안 동일한 말씀 유지)
  const currentVerse = getCurrentVerse();
  if (currentVerse) {
    console.log('📖 기존 말씀 사용:', currentVerse.reference);
    return currentVerse;
  }
  
  const usedIds = getUsedVerses();
  const availableVerses = ALL_VERSES.filter(verse => !usedIds.includes(verse.id));
  
  // 모든 말씀을 다 사용했다면 리셋
  if (availableVerses.length === 0) {
    console.log('🔄 모든 말씀 사용 완료 - 목록 리셋');
    setUsedVerses([]);
    return getRandomVerse(); // 재귀 호출로 다시 선택
  }
  
  // 랜덤 선택
  const randomIndex = Math.floor(Math.random() * availableVerses.length);
  const selectedVerse = availableVerses[randomIndex];
  
  // 사용된 말씀 목록에 추가
  const newUsedIds = [...usedIds, selectedVerse.id];
  setUsedVerses(newUsedIds);
  setCurrentVerse(selectedVerse);
  
  console.log('✨ 새 말씀 선택:', {
    reference: selectedVerse.reference,
    used: newUsedIds.length,
    remaining: ALL_VERSES.length - newUsedIds.length
  });
  
  return selectedVerse;
};

// 강제로 새로운 말씀 선택 (사용자가 수정 원할 때)
export const getNewRandomVerse = (): Verse => {
  checkAndResetIfNewDay();
  
  const usedIds = getUsedVerses();
  const availableVerses = ALL_VERSES.filter(verse => !usedIds.includes(verse.id));
  
  // 모든 말씀을 다 사용했다면 리셋
  if (availableVerses.length === 0) {
    console.log('🔄 모든 말씀 사용 완료 - 목록 리셋');
    setUsedVerses([]);
    return getNewRandomVerse(); // 재귀 호출로 다시 선택
  }
  
  // 랜덤 선택
  const randomIndex = Math.floor(Math.random() * availableVerses.length);
  const selectedVerse = availableVerses[randomIndex];
  
  // 사용된 말씀 목록에 추가
  const newUsedIds = [...usedIds, selectedVerse.id];
  setUsedVerses(newUsedIds);
  setCurrentVerse(selectedVerse);
  
  console.log('🔄 새 말씀 강제 선택:', {
    reference: selectedVerse.reference,
    used: newUsedIds.length,
    remaining: ALL_VERSES.length - newUsedIds.length
  });
  
  return selectedVerse;
};

// 사용자 정의 말씀 설정
export const setCustomVerse = (verse: { reference: string; text: string }): void => {
  const customVerse: Verse = {
    id: -1, // 사용자 정의 말씀은 -1로 표시
    reference: verse.reference,
    text: verse.text
  };
  
  setCurrentVerse(customVerse);
  console.log('✏️ 사용자 정의 말씀 설정:', customVerse.reference);
};

// 현재 말씀이 사용자 정의인지 확인
export const isCustomVerse = (): boolean => {
  const current = getCurrentVerse();
  return current ? current.id === -1 : false;
};

// 통계 정보 가져오기
export const getVerseStats = () => {
  const usedIds = getUsedVerses();
  const totalVerses = ALL_VERSES.length;
  const usedCount = usedIds.length;
  const remainingCount = totalVerses - usedCount;
  
  return {
    total: totalVerses,
    used: usedCount,
    remaining: remainingCount,
    percentage: Math.round((usedCount / totalVerses) * 100)
  };
};
